<template>
  <a
    class="mb-1 text-truncate w-100 el-button el-button--small d-flex justify-content-center"
    :class="`el-button--${button.buttonType} expanded_btn_row_item__${buttonIndex}`"
    @click="onClick"
    :href="url"
    :target="button.target"
    :data-exp-btn-content="buttonName"
    v-bind="dynamicBinds"
  >
    <icon v-if="icon" :icon="icon" class="iconify-icons md-12 me-2" />
    <span>{{ buttonName }}</span>
  </a>
</template>

<script>
  import { mapActions } from 'vuex'
  import { get, map } from 'lodash'
  import confirmation from '../../../../utils/mixin-confirmation'
  import EventBus from '../../../../utils/event-bus'
  import axios from '../../../../axiosClient'
  import mixinNotify from '../../../../utils/mixin-notify'
  import { TARGET_BLANK } from '../../../../consts'

  export default {
    name: 'ExpandedRowButton',
    mixins: [confirmation, mixinNotify],
    props: {
      button: {
        type: Object,
        default: () => ({}),
      },
      buttonIndex: {
        type: [String, Number],
        default: () => null,
      },
    },
    data() {
      return {
        pending: false,
      }
    },
    computed: {
      dynamicBinds() {
        return this.button.download ? { download: '' } : {}
      },
      actionButtonsStyle() {
        return get(this.extendedOptions, 'actionButtonsStyle')
      },
      actionsInlineVisible() {
        return this.showActionButtons && this.actionButtonsStyle === 'Inline'
      },
      buttonName() {
        return typeof this.name === 'string'
          ? this.$i18n.t(this.name)
          : this.$i18n.interpolate(
              this.$i18n.t(this.name.text, { category: 'ExpandedRowButton' }),
              this.translateParams(this.name.params)
            )
      },
      buttonConfirm() {
        return get(this.button, 'confirm', { active: false })
      },
      extendedOptions() {
        return this.$get('extendedOptions')
      },
      formType() {
        return this.button.formType
      },
      hiddenSearch() {
        return this.$get('hiddenSearch')
      },
      icon() {
        return this.button.icon
      },
      isDetailViewVisible() {
        const showDetailView = this.rowOptions ? this.showDetailView : false
        if (showDetailView) {
          switch (this.viewMode) {
            case 'cardView':
            case 'detailView':
              return true
            case 'gridView':
              return this.singleLineGrid || !this.actionsInlineVisible
            default:
              return this.isInModal || !this.isVersionList
          }
        }
        return false
      },
      isInModal() {
        return this.$get('isInModal')
      },
      isPopup() {
        return this.button.popup
      },
      isVersionList() {
        return this.$get('isVersionList')
      },
      name() {
        return get(this.button, 'name', '')
      },
      showActionButtons() {
        return this.$get('visibilityState')({ key: 'showActionButtons' })
      },
      showDetailView() {
        return this.$get('visibilityState')({ key: 'showDetailView' })
      },
      singleLineGrid() {
        return this.hiddenSearch.where ? this.hiddenSearch.where.length === 1 : false
      },
      target() {
        return this.button.target || '_self'
      },
      url() {
        if (this.button.params) {
          let tmpUrl = this.button.url
          map(this.button.params, (value, key) => {
            const coreParam = get(window.CORE.params, value.replace(':', ''))
            if (coreParam) {
              value = coreParam
            }
            tmpUrl += `&${key}=${value}`
          })
          return tmpUrl
        }
        return this.button.url
      },
      viewMode() {
        return this.$get('viewMode')
      },
    },
    methods: {
      ...mapActions('popup', ['setOpened', 'setUrl', 'setType']),
      async onClick(event) {
        if (this.button.download) {
          return
        }

        event.preventDefault()

        if (this.isPopup) {
          this.setUrl({ url: this.url, event: this.button.emitEvent })
          this.setType(this.formType)
          this.setOpened()
          EventBus.$emit('refresh-form-expression', {})
        } else {
          await this.handleConfirmation()
        }
      },
      parseResponse(response) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = function () {
            const text = reader.result
            try {
              const json = JSON.parse(text)
              resolve(json)
            } catch (error) {
              resolve({ location: null })
            }
          }
          reader.onerror = function () {
            reject(new Error('Error reading blob'))
          }
          reader.readAsText(response)
        })
      },
      async handleConfirmation() {
        let confirmed = true
        if (this.buttonConfirm.active) {
          confirmed = await this._confirm(get(this.buttonConfirm, 'message'))
        }

        if (confirmed) {
          const response = await axios()
            .get(this.url, {
              params: { redirect: false },
              responseType: 'blob',
              withCredentials: true,
              headers: {
                Accept: '*/*',
              },
            }).catch((error) => ({ error }))

          if (response.error) {
            return this.showNotification({
              title: this.$i18n.t('error_occurred'),
              text: this.$i18n.t('error_occurred: ${buttonName}'),
              params: {
                buttonName: this.buttonName,
              },
              type: 'error',
            })
          }

          let redirectUrl
          const contentDisposition = get(response, ['headers', 'content-disposition'])
          if (contentDisposition && contentDisposition.includes('attachment')) {
            const fileName = get(response, ['headers', 'content-disposition'], '').split('filename=')[1]
            const type = get(response, ['headers', 'content-type'], '')
            const blob = new Blob([response.data], { type })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
          } else {
            // ha nem file-t töltünk le, kell "átirányítás"
            const parsedResponse = await this.parseResponse(response.data)
            redirectUrl = get(parsedResponse, 'location') || this.url
          }

          if (!redirectUrl) return

          if (get(this.button, 'target') === TARGET_BLANK) {
            return window.open(redirectUrl)
          }

          if ([window.location.pathname, window.location.href].includes(redirectUrl)) {
            this.$router.push({
              path: window.location.pathname,
              query: {
                ...this.$route.query,
                lastRefresh: Date.now(),
              },
            })
          } else {
            this.$router.push(redirectUrl)
          }
        }
      },
      translateParams(params) {
        Object.keys(params).forEach((key) => {
          params[key] = this.$i18n.t(params[key])
        })
        return params
      },
    },
  }
</script>

<style>
  a {
    text-decoration: none;
  }
</style>
